import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { useGetBrameCampaignQuery } from 'api/brameCampaigns'
import brameImage from 'assets/images/cms/app-home-page/brame.svg'
import { buildTranslate } from 'locales'
import React from 'react'
import { ContentBlockData } from 'slices/appLandingPageTheme/types'
import ConfigurationInfo from './ConfigurationInfo'
import { getImageUrl } from './helper'

type Props = {
  contentBlock: ContentBlockData
}

const t = buildTranslate('cms.content.app_home_page.content.block_item')

const GameDescription = ({ contentBlock }: Props) => {
  const [css] = useStyletron()

  const isGameBlock = contentBlock.type === 'game'
  const gameSettings = isGameBlock ? contentBlock.settings : null
  const hasImage =
    isGameBlock && 'image' in contentBlock && getImageUrl(contentBlock.image)

  const campaignId =
    isGameBlock &&
    'settings' in contentBlock &&
    contentBlock.settings.brame_campaign_id
      ? String(contentBlock.settings.brame_campaign_id)
      : ''

  const { data: selectedCampaign, error: campaignError } =
    useGetBrameCampaignQuery(campaignId, {
      skip: !campaignId,
    })

  const hasCampaign = campaignId && selectedCampaign && !campaignError

  const hasTargeting =
    ('target_segments' in contentBlock &&
      contentBlock.target_segments &&
      contentBlock.target_segments.length > 0) ||
    ('audience_type' in contentBlock &&
      contentBlock.audience_type === 'select_segment' &&
      'segment_id' in contentBlock &&
      contentBlock.segment_id) ||
    ('start_at' in contentBlock && contentBlock.start_at) ||
    ('end_at' in contentBlock && contentBlock.end_at)

  // Only show default text when there are no configured details
  const hasAnyConfiguration = hasImage || hasCampaign || hasTargeting

  if (!isGameBlock || !gameSettings) return <></>

  return (
    <div className="mt-s">
      {hasImage && (
        <div className="mb-l">
          <img
            src={getImageUrl(contentBlock.image)!}
            alt="Game"
            className={css({
              height: '120px',
              width: '201px',
              borderRadius: '4px',
              objectFit: 'cover',
              border: '1px solid #e9eaec',
            })}
          />
        </div>
      )}

      <div className="d-flex flex-column gap-2">
        {/* Only show default text when there are no configured details */}
        {!hasAnyConfiguration && (
          <Text.BodyText3 color="grey70" className="mb-s">
            {t('game_default_description')}
          </Text.BodyText3>
        )}

        {/* Show 'This block links to' format when campaign is configured */}
        {process.env.NODE_ENV === 'development' && campaignError && (
          <div className="mb-2">
            <Text.BodyText4 tag="p" color="red">
              [DEBUG] Campaign API Error: {JSON.stringify(campaignError)}
            </Text.BodyText4>
          </div>
        )}
        {selectedCampaign && !campaignError && (
          <div className="mb-2">
            <Text.BodyText4 tag="p" color="grey90" bold className="mb-xs">
              {t('description_link.link_description')}
            </Text.BodyText4>
            <Text.BodyText4 tag="p" color="grey70">
              {selectedCampaign.name}
            </Text.BodyText4>
            {selectedCampaign.campaign_url && (
              <Text.BodyText4
                tag="p"
                color="grey70"
                className={css({ overflowWrap: 'anywhere' })}
              >
                {selectedCampaign.campaign_url}
              </Text.BodyText4>
            )}
          </div>
        )}

        <ConfigurationInfo contentBlock={contentBlock} />
      </div>

      <div className={`d-flex align-items-center mt-s ${css({ gap: '4px' })}`}>
        <Text.BodyText3 color="grey70" className={css({ fontSize: '14px' })}>
          {t('powered_by')}
        </Text.BodyText3>
        <img
          src={brameImage}
          alt="BRAME"
          className={css({ height: '14px', width: '60px' })}
        />
      </div>
    </div>
  )
}

export default GameDescription
